import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, FilterX, Landmark, Network, Search } from "lucide-react";
import { SecondaryButton, StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { DataTable } from "../components/DataTable";
import { SafeMultisigTransaction } from "../components/SafeMultisigTransaction";
import { amount, exportCsv, shortAddress } from "../format";

type Deposit = {
  flow_id: string; user_address: string; amount: string; tx_hash: string;
  block_number: string; confirmation_count: number; flow_status: number;
  detected_at: string; maturity_at: string | null;
  deposit_source: "NODE" | "MARKET" | "MARKET_TOP_UP";
  usdt_amount: string; iage_amount: string;
  product_name: string; lock_days: number; contract_position_id?: string | null;
};
type Withdrawal = {
  withdraw_id: string; user_address: string; withdrawal_type: number; requested_amount: string;
  fee_amount: string; penalty_amount: string; net_amount: string; withdrawal_status: number; request_time: string;
  tx_hash: string | null;
  chainId?: number;
  treasuryAddress?: `0x${string}`;
  approvalCall?: {
    contract: string; functionName: string; spender: string;
    amount: string; amountRaw: string; calldata: string;
  };
  principalContractCall?: {
    contract: string; functionName: string; withdrawalId: string; recipient: string;
    requestedAmount: string; penaltyAmount: string; feeBasis: string;
    requestedAmountRaw: string; penaltyAmountRaw: string; feeBasisRaw: string;
    calldata: string;
  };
  marketEarlyExitCall?: {
    contract: string; functionName: "executeEarlyExit";
    positionId: string; calldata: string;
  };
};
type TreasuryFlow = {
  treasury_flow_id: string; token_symbol: "USDT" | "SAGE" | "IAGE";
  token_address: string; from_address: string; to_address: string;
  direction: "IN" | "OUT"; amount: string; purpose: string;
  counterparty_address: string; counterparty_role: string;
  tx_hash: string; log_index: number; block_number: string;
  block_time: string; confirmation_count: number; confirmation_status: "CONFIRMED";
};
type TreasuryFlowResponse = {
  items: TreasuryFlow[]; total: number;
  summary: {
    treasuryAddress: string;
    balances: Record<"USDT" | "SAGE" | "IAGE", string>;
    last24Hours: Record<"USDT" | "SAGE" | "IAGE", {
      inflow: string; outflow: string; net: string;
    }>;
    indexedBlock: string | null; snapshotAt: string | null;
    confirmationThreshold: number;
  };
};

function PrincipalSubmission({ withdrawal }: { withdrawal: Withdrawal }) {
  const [txHash, setTxHash] = useState("");
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => adminApi(`/withdrawals/${withdrawal.withdraw_id}/principal-submission`, {
      method: "POST",
      body: JSON.stringify({ txHash })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-flow", "withdrawals"] })
  });
  if (withdrawal.withdrawal_type === 4 && withdrawal.marketEarlyExitCall) {
    return (
      <details>
        <summary>查看 Market 违约退出多签参数</summary>
        <code>
          Treasury {withdrawal.treasuryAddress}<br />
          目标合约 {withdrawal.marketEarlyExitCall.contract}<br />
          方法 {withdrawal.marketEarlyExitCall.functionName}<br />
          Market 仓位 #{withdrawal.marketEarlyExitCall.positionId}<br />
          calldata {withdrawal.marketEarlyExitCall.calldata}
        </code>
        {withdrawal.chainId === 56 && withdrawal.treasuryAddress ? (
          <SafeMultisigTransaction
            label={`批准 Market 仓位 #${withdrawal.marketEarlyExitCall.positionId} 违约退出`}
            safe={withdrawal.treasuryAddress}
            to={withdrawal.marketEarlyExitCall.contract as `0x${string}`}
            data={withdrawal.marketEarlyExitCall.calldata as `0x${string}`}
            onExecuted={() => queryClient.invalidateQueries({ queryKey: ["admin-flow", "withdrawals"] })}
          />
        ) : (
          <p className="cell-subline">本地链 / BSC Testnet 使用测试 Treasury 2/3 EIP-712 执行脚本；链上确认后索引器会自动移除此申请。</p>
        )}
      </details>
    );
  }
  if (withdrawal.withdrawal_type !== 3 || ![1, 2].includes(withdrawal.withdrawal_status)) {
    return withdrawal.tx_hash ? <code>{shortAddress(withdrawal.tx_hash)}</code> : <span>—</span>;
  }
  const valid = /^0x[a-fA-F0-9]{64}$/.test(txHash);
  return (
    <div className="tx-submission">
      {withdrawal.principalContractCall && (
        <details>
          <summary>查看 TP 多签授权与原子退款参数</summary>
          <code>
            {withdrawal.approvalCall && <>
              第一步：USDT 授权<br />
              授权合约 {withdrawal.approvalCall.contract}<br />
              方法 {withdrawal.approvalCall.functionName}<br />
              spender {withdrawal.approvalCall.spender}<br />
              授权额度 {amount(withdrawal.approvalCall.amount)} USDT<br />
              授权额度原始值 {withdrawal.approvalCall.amountRaw}<br />
              授权 calldata {withdrawal.approvalCall.calldata}<br /><br />
            </>}
            第二步：本金原子退款<br />
            合约 {withdrawal.principalContractCall.contract}<br />
            方法 {withdrawal.principalContractCall.functionName}<br />
            withdrawalId {withdrawal.principalContractCall.withdrawalId}<br />
            recipient {withdrawal.principalContractCall.recipient}<br />
            requestedAmount {amount(withdrawal.principalContractCall.requestedAmount)}<br />
            penaltyAmount {amount(withdrawal.principalContractCall.penaltyAmount)}<br />
            feeBasis {amount(withdrawal.principalContractCall.feeBasis)}<br />
            requestedAmount 原始值 {withdrawal.principalContractCall.requestedAmountRaw}<br />
            penaltyAmount 原始值 {withdrawal.principalContractCall.penaltyAmountRaw}<br />
            feeBasis 原始值 {withdrawal.principalContractCall.feeBasisRaw}<br />
            退款 calldata {withdrawal.principalContractCall.calldata}
          </code>
          {withdrawal.treasuryAddress && withdrawal.approvalCall && (
            <SafeMultisigTransaction
              label="第一步：USDT 授权（确认成功后再创建第二步）"
              safe={withdrawal.treasuryAddress}
              to={withdrawal.approvalCall.contract as `0x${string}`}
              data={withdrawal.approvalCall.calldata as `0x${string}`}
            />
          )}
          {withdrawal.treasuryAddress && (
            <SafeMultisigTransaction
              label="第二步：本金原子退款"
              safe={withdrawal.treasuryAddress}
              to={withdrawal.principalContractCall.contract as `0x${string}`}
              data={withdrawal.principalContractCall.calldata as `0x${string}`}
              onExecuted={(hash) => setTxHash(hash)}
            />
          )}
        </details>
      )}
      <input
        value={txHash}
        onChange={(event) => setTxHash(event.target.value)}
        placeholder="粘贴原子退款交易哈希"
        aria-label={`本金退出 WD-${withdrawal.withdraw_id} 交易哈希`}
      />
      <SecondaryButton disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? "登记中…" : "登记"}
      </SecondaryButton>
      {mutation.error && <span className="cell-error">{mutation.error.message}</span>}
    </div>
  );
}

export function FlowListPage({ kind }: { kind: "deposits" | "withdrawals" | "treasury" }) {
  const isTreasuryView = kind === "treasury";
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("");
  const [depositLedgerChoice, setDepositLedger] = useState<"MARKET" | "NODE">("MARKET");
  const depositLedger = isTreasuryView ? "TREASURY" : depositLedgerChoice;
  const [treasuryToken, setTreasuryToken] = useState("");
  const [treasuryDirection, setTreasuryDirection] = useState("");
  const params = new URLSearchParams({ page: "1", pageSize: "100" });
  if (address) params.set("address", address);
  if (status) params.set("status", status);
  const query = useQuery({
    queryKey: ["admin-flow", kind, address, status],
    queryFn: () => adminApi<{ items: Array<Deposit | Withdrawal> }>(
      `/${kind === "treasury" ? "deposits" : kind}?${params}`
    ),
    enabled: !isTreasuryView,
    refetchInterval: 5000
  });
  const treasuryParams = new URLSearchParams({ page: "1", pageSize: "100" });
  if (address) treasuryParams.set("address", address);
  if (treasuryToken) treasuryParams.set("token", treasuryToken);
  if (treasuryDirection) treasuryParams.set("direction", treasuryDirection);
  const treasuryQuery = useQuery({
    queryKey: ["admin-flow", "treasury-token", address, treasuryToken, treasuryDirection],
    queryFn: () => adminApi<TreasuryFlowResponse>(`/treasury-token-flows?${treasuryParams}`),
    enabled: isTreasuryView,
    refetchInterval: 5000
  });

  const columns = useMemo<ColumnDef<Deposit | Withdrawal>[]>(() => kind === "deposits" ? [
    { header: depositLedger === "MARKET" ? "产品 / 仓位" : "节点周期", cell: ({ row }) => { const value = row.original as Deposit; return <div><StatusPill tone={depositLedger === "MARKET" ? "info" : "success"}>{depositLedger === "MARKET" ? (value.deposit_source === "MARKET_TOP_UP" ? "Market 加仓" : "Market 参与") : "节点质押"}</StatusPill><small className="cell-subline">{value.product_name} · {value.lock_days} 天{value.contract_position_id ? ` · #${value.contract_position_id}` : ""}</small></div>; } },
    { header: "钱包地址", accessorKey: "user_address", cell: ({ getValue }) => <code>{shortAddress(getValue<string>())}</code> },
    { header: "入金 / 资金拆分", cell: ({ row }) => { const value = row.original as Deposit; return <div className="numeric"><b>{amount(value.amount)} USDT 计价</b><small className="cell-subline">{amount(value.usdt_amount)} USDT + {amount(value.iage_amount)} IAGE</small></div>; } },
    { header: "交易哈希", accessorKey: "tx_hash", cell: ({ getValue }) => <code>{shortAddress(getValue<string>())}</code> },
    { header: "区块 / 确认", cell: ({ row }) => <span className="numeric">{(row.original as Deposit).block_number} / {(row.original as Deposit).confirmation_count}</span> },
    { header: "状态", accessorKey: "flow_status", cell: ({ getValue }) => {
      const value = Number(getValue<number | string>());
      const label = value === 1 ? "已激活" : value === 2 ? "未激活" : value === 3 ? "链重组" : "待确认";
      const tone = value === 1 ? "success" : value === 2 || value === 3 ? "danger" : "warning";
      return <StatusPill tone={tone}>{label}</StatusPill>;
    } },
    { header: "检测时间", accessorKey: "detected_at", cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString("zh-CN", { hour12: false }) }
  ] : [
    { header: "申请编号", accessorKey: "withdraw_id", cell: ({ row }) => <code>{(row.original as Withdrawal).withdrawal_type === 4 ? String((row.original as Withdrawal).withdraw_id).toUpperCase() : `WD-${(row.original as Withdrawal).withdraw_id}`}</code> },
    { header: "钱包地址", accessorKey: "user_address", cell: ({ getValue }) => <code>{shortAddress(getValue<string>())}</code> },
    { header: "类型", accessorKey: "withdrawal_type", cell: ({ getValue }) => {
      const type = getValue<number>();
      return type === 1 ? "直推收益" : type === 2 ? "手续费分红" : type === 4 ? "Market 违约本金" : "节点本金";
    } },
    { header: "申请 / 罚扣 / 费用 / 净额", cell: ({ row }) => { const value = row.original as Withdrawal; return <span className="numeric">{amount(value.requested_amount)} / {amount(value.penalty_amount)} / {amount(value.fee_amount)} / {amount(value.net_amount)}</span>; } },
    { header: "状态", accessorKey: "withdrawal_status", cell: ({ getValue }) => {
      const value = getValue<number>();
      const label = value === 1 ? "多签复核" : value === 3 ? "链上确认中" : value === 4 ? "已确认" : value === 9 ? "等待流动性" : "处理中";
      return <StatusPill tone={value === 4 ? "success" : value === 9 ? "warning" : "info"}>{label}</StatusPill>;
    } },
    { header: "申请时间", accessorKey: "request_time", cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString("zh-CN", { hour12: false }) },
    { header: "多签原子退款交易", cell: ({ row }) => <PrincipalSubmission withdrawal={row.original as Withdrawal} /> }
  ], [depositLedger, kind]);

  const treasuryColumns = useMemo<ColumnDef<TreasuryFlow>[]>(() => [
    { header: "链上时间", accessorKey: "block_time", cell: ({ getValue }) =>
      new Date(getValue<string>()).toLocaleString("zh-CN", { hour12: false }) },
    { header: "Token / 方向", cell: ({ row }) => <div className="treasury-token-direction">
      <span className={`token-chip token-${row.original.token_symbol.toLowerCase()}`}>
        {row.original.token_symbol}
      </span>
      <StatusPill tone={row.original.direction === "IN" ? "success" : "info"}>
        {row.original.direction === "IN" ? "流入" : "流出"}
      </StatusPill>
    </div> },
    { header: "数量", cell: ({ row }) => <strong className={`treasury-flow-amount is-${row.original.direction.toLowerCase()}`}>
      {row.original.direction === "IN" ? "+" : "−"}{amount(row.original.amount)} {row.original.token_symbol}
    </strong> },
    { header: "用途", accessorKey: "purpose", cell: ({ row }) => <div>
      <b>{row.original.purpose}</b>
      <small className="cell-subline">{row.original.counterparty_role}</small>
    </div> },
    { header: "对手方", accessorKey: "counterparty_address", cell: ({ getValue }) =>
      <code>{shortAddress(getValue<string>())}</code> },
    { header: "交易哈希", accessorKey: "tx_hash", cell: ({ getValue }) =>
      <code>{shortAddress(getValue<string>())}</code> },
    { header: "区块 / 确认", cell: ({ row }) => <div className="numeric">
      <b>#{row.original.block_number}</b>
      <small className="cell-subline">{row.original.confirmation_count} 次 · 已确认</small>
    </div> }
  ], []);

  const items = query.data?.items ?? [];
  const deposits = kind === "deposits" ? items as Deposit[] : [];
  const marketDeposits = deposits.filter((item) => item.deposit_source !== "NODE");
  const nodeDeposits = deposits.filter((item) => item.deposit_source === "NODE");
  const selectedItems = kind === "deposits"
    ? (depositLedger === "MARKET" ? marketDeposits : depositLedger === "NODE" ? nodeDeposits : [])
    : items;
  const treasuryItems = treasuryQuery.data?.items ?? [];
  const ledgerSummary = (rows: Deposit[]) => {
    const activeRows = rows.filter((row) => Number(row.flow_status) === 1);
    return {
      count: rows.length,
      active: activeRows.length,
      total: activeRows.reduce((sum, row) => sum + Number(row.amount), 0),
      usdt: activeRows.reduce((sum, row) => sum + Number(row.usdt_amount), 0),
      iage: activeRows.reduce((sum, row) => sum + Number(row.iage_amount), 0)
    };
  };
  const marketSummary = ledgerSummary(marketDeposits);
  const nodeSummary = ledgerSummary(nodeDeposits);
  const visibleItems = depositLedger === "TREASURY" ? treasuryItems : selectedItems;
  const visibleUpdatedAt = depositLedger === "TREASURY"
    ? treasuryQuery.dataUpdatedAt : query.dataUpdatedAt;
  return (
    <div className="list-page">
      <section className={`filter-bar${depositLedger === "TREASURY" ? " is-treasury" : ""}`}>
        <label><Search size={16} /><input value={address} onChange={(event) => setAddress(event.target.value)} placeholder={depositLedger === "TREASURY" ? "输入完整对手方地址" : "输入完整钱包地址"} /></label>
        {depositLedger === "TREASURY" ? <>
          <select value={treasuryToken} onChange={(event) => setTreasuryToken(event.target.value)} aria-label="Token 筛选">
            <option value="">全部 Token</option>
            <option value="USDT">USDT</option>
            <option value="SAGE">SAGE</option>
            <option value="IAGE">IAGE</option>
          </select>
          <select value={treasuryDirection} onChange={(event) => setTreasuryDirection(event.target.value)} aria-label="方向筛选">
            <option value="">全部方向</option>
            <option value="1">流入 Treasury</option>
            <option value="2">流出 Treasury</option>
          </select>
        </> : <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="状态筛选">
          <option value="">全部状态</option>
          <option value="0">待确认 / 已申请</option>
          <option value="1">{kind === "deposits" ? "已激活" : "已确认"}</option>
          {kind === "deposits" && <><option value="2">未激活</option><option value="3">链重组</option></>}
          {kind === "withdrawals" && <option value="9">等待流动性</option>}
        </select>}
        <SecondaryButton onClick={() => { setAddress(""); setStatus(""); setTreasuryToken(""); setTreasuryDirection(""); }}><FilterX size={16} />清除筛选</SecondaryButton>
        <SecondaryButton onClick={() => exportCsv(
          kind === "deposits" ? `ocs-${depositLedger.toLowerCase()}-${depositLedger === "TREASURY" ? "token-flows" : "deposits"}.csv` : `ocs-${kind}.csv`,
          visibleItems as unknown as Record<string, unknown>[]
        )}><Download size={16} />导出 CSV</SecondaryButton>
      </section>
      {kind === "deposits" && (
        <section className="deposit-ledger-tabs" role="tablist" aria-label="入金账本">
          <button
            type="button"
            role="tab"
            aria-selected={depositLedger === "MARKET"}
            className={depositLedger === "MARKET" ? "is-active" : ""}
            onClick={() => setDepositLedger("MARKET")}
          >
            <header><Landmark size={18} /><span>MARKET LEDGER</span><strong>Market 入金</strong></header>
            <dl>
              <div><dt>当前记录</dt><dd>{marketSummary.count}</dd></div>
              <div><dt>已激活</dt><dd>{marketSummary.active}</dd></div>
              <div><dt>已确认计价</dt><dd>{amount(marketSummary.total)}</dd></div>
            </dl>
            <small>{amount(marketSummary.usdt)} USDT · {amount(marketSummary.iage)} IAGE</small>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={depositLedger === "NODE"}
            className={depositLedger === "NODE" ? "is-active" : ""}
            onClick={() => setDepositLedger("NODE")}
          >
            <header><Network size={18} /><span>NODE LEDGER</span><strong>节点入金</strong></header>
            <dl>
              <div><dt>当前记录</dt><dd>{nodeSummary.count}</dd></div>
              <div><dt>已激活</dt><dd>{nodeSummary.active}</dd></div>
              <div><dt>已确认本金</dt><dd>{amount(nodeSummary.total)}</dd></div>
            </dl>
            <small>固定 1,000 USDT · 180 天节点周期</small>
          </button>
        </section>
      )}
      {isTreasuryView && (
        <section className="treasury-balance-strip" aria-label="Treasury Token 当前余额">
          <header>
            <div><p>TREASURY BALANCE</p><code>{treasuryQuery.data?.summary.treasuryAddress ?? "等待链上快照"}</code></div>
            <span>索引区块 #{treasuryQuery.data?.summary.indexedBlock ?? "—"} · {treasuryQuery.data?.total ?? 0} 笔确认流水</span>
          </header>
          <dl>
            {(["USDT", "SAGE", "IAGE"] as const).map((symbol) => <div key={symbol}>
              <dt><span className={`token-chip token-${symbol.toLowerCase()}`}>{symbol}</span>当前余额</dt>
              <dd>{amount(treasuryQuery.data?.summary.balances[symbol])}</dd>
            </div>)}
          </dl>
        </section>
      )}
      {isTreasuryView && (
        <section className="treasury-flow-summary" aria-label="Treasury 过去 24 小时 Token 流量">
          {(["USDT", "SAGE", "IAGE"] as const).map((symbol) => {
            const flow = treasuryQuery.data?.summary.last24Hours[symbol]
              ?? { inflow: "0", outflow: "0", net: "0" };
            const negative = flow.net.startsWith("-");
            return <article key={symbol}>
              <header><span className={`token-chip token-${symbol.toLowerCase()}`}>{symbol}</span><small>过去 24 小时</small></header>
              <strong className={negative ? "is-negative" : "is-positive"}>
                {negative ? "−" : "+"}{amount(negative ? flow.net.slice(1) : flow.net)}
              </strong>
              <dl>
                <div><dt>流入</dt><dd>+{amount(flow.inflow)}</dd></div>
                <div><dt>流出</dt><dd>−{amount(flow.outflow)}</dd></div>
              </dl>
            </article>;
          })}
        </section>
      )}
      <div className="list-meta">
        <span>{kind === "deposits" ? `${depositLedger === "MARKET" ? "Market" : "节点"} · ` : isTreasuryView ? "Treasury Token · " : ""}{isTreasuryView ? treasuryQuery.data?.total ?? 0 : selectedItems.length} 条记录</span>
        <span>自动刷新 · {visibleUpdatedAt ? new Date(visibleUpdatedAt).toLocaleTimeString("zh-CN", { hour12: false }) : "—"}</span>
      </div>
      {isTreasuryView ? <DataTable
        data={treasuryItems}
        columns={treasuryColumns}
        emptyMessage="Treasury 的 USDT 链上变化将在确认后显示；任何 SAGE/IAGE 变动都将作为异常流水保留。"
      /> : <DataTable
        data={selectedItems}
        columns={columns}
        emptyMessage={kind === "deposits"
          ? `${depositLedger === "MARKET" ? "Market" : "节点"} 入金确认后会显示在这个独立账本中。`
          : "提现申请创建后会显示状态与流动性进度。"}
      />}
    </div>
  );
}
